import React from 'react';
import {
    useLocalParticipant,
    useTrackTranscription,
} from "@livekit/components-react";
import World from "components/World";
import { Track } from "livekit-client";
import { useEffect, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { fetchResources } from "store/communication/actions/speechToTextServiceActions";

const TranscriptionTile = React.memo(({ agentAudioTrack, onTranscript }) => {
    const dispatch = useDispatch();

    const agentMessages = useTrackTranscription(agentAudioTrack);
    // `useLocalParticipant()` API shape has varied across @livekit/components-react versions.
    // Support both:
    // - { localParticipant, microphoneTrack }
    // - { localParticipant: { ... }, microphoneTrack: ... } (same)
    // - legacy: { localParticipant, microphoneTrack } but callers might mistakenly treat
    //   the whole return as a participant.
    const local = useLocalParticipant();
    const lkLocalParticipant =
        local?.localParticipant || local?.participant || local?.localParticipant?.localParticipant || null;
    const micPublication =
        local?.microphoneTrack ||
        local?.microphonePublication ||
        local?.localParticipant?.microphoneTrack ||
        lkLocalParticipant?.getTrackPublication?.(Track.Source.Microphone) ||
        null;

    const localMessages = useTrackTranscription(
        micPublication
            ? {
                publication: micPublication,
                source: Track.Source.Microphone,
                participant: lkLocalParticipant,
            }
            : // Fall back to a minimal reference; if transcription can't run without the
            // publication, this at least avoids crashing and logs below.
            {
                source: Track.Source.Microphone,
                participant: lkLocalParticipant,
            }
    );
    const timeoutRef = React.useRef(null); // Ref to store the timeout id
    const warnedNoMicRef = React.useRef(false);
    const agentSeenRef = React.useRef(new Map()); // id -> { text, final }
    const localSeenRef = React.useRef(new Map()); // id -> { text, final }
    const lastLocalSegCountRef = React.useRef(0);

    // Callback to dispatch fetchResources only when necessary
    const handleAgentFinalMessage = useCallback(
        (id, text) => {
            console.log("Sending Agent Transcript:", {
                id,
                text,
                final: true,
                participant: "tutor",
            });
            World.runCommand(
                `livekit_final_response ${id} ${text.replace(/ /g, "_")} true tutor`
            );
            if (typeof onTranscript === 'function') {
                onTranscript({ id, text, final: true, participant: 'tutor' });
            }

        },
        [dispatch, onTranscript]
    );

    const handleLocalMessage = useCallback((id, text, isFinal) => {
        console.log("Sending Local Transcript:", {
            id,
            text,
            final: isFinal,
            participant: "player",
        });
        World.runCommand(
            `livekit_final_response ${id} ${text.replace(/ /g, "_")} ${isFinal} player`
        );
        if (typeof onTranscript === 'function') {
            onTranscript({ id, text, final: Boolean(isFinal), participant: 'player' });
        }
        if (isFinal) {
            // If there's a previous timeout, clear it
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            // Set a new timeout for 1 second
            timeoutRef.current = setTimeout(() => {
                dispatch(fetchResources());
            }, 2 * 1000); // 2-second delay before dispatching the API call
        }

    }, [dispatch, onTranscript]);

    // Process agent messages
    useEffect(() => {
        agentMessages.segments.forEach((s) => {
            const prev = agentSeenRef.current.get(s.id);
            const text = s.text || '';
            const final = Boolean(s.final);
            // Avoid spamming Unity with duplicates; only send if content changed
            // or if this is the first time we see this id.
            if (prev && prev.text === text && prev.final === final) return;
            agentSeenRef.current.set(s.id, { text, final });

            if (final) {
                handleAgentFinalMessage(s.id, text);
            } else {
                World.runCommand(
                    `livekit_final_response ${s.id} ${text.replace(/ /g, "_")} false tutor`
                );
                if (typeof onTranscript === 'function') {
                    onTranscript({ id: s.id, text, final: false, participant: 'tutor' });
                }
            }
        });
    }, [agentMessages.segments, handleAgentFinalMessage]);

    // Process local participant (customer) messages
    useEffect(() => {
        if (!micPublication && !warnedNoMicRef.current) {
            warnedNoMicRef.current = true;
            console.warn(
                "[LiveKit] No local microphone publication available for transcription. " +
                "Local final transcripts (and tutor responses) may never trigger.",
                { localKeys: Object.keys(local || {}), hasLocalParticipant: !!lkLocalParticipant }
            );
        }

        // Helpful debug: if user reports "no response", check whether local transcription is producing *any* segments.
        if (
            localMessages?.segments &&
            localMessages.segments.length !== lastLocalSegCountRef.current
        ) {
            lastLocalSegCountRef.current = localMessages.segments.length;
            console.log('[LiveKit] local transcription segments', {
                count: localMessages.segments.length,
                lastId: localMessages.segments.at?.(-1)?.id,
                lastFinal: Boolean(localMessages.segments.at?.(-1)?.final),
            });
        }

        localMessages.segments.forEach((s) => {
            const prev = localSeenRef.current.get(s.id);
            const text = s.text || '';
            const final = Boolean(s.final);
            if (prev && prev.text === text && prev.final === final) return;
            localSeenRef.current.set(s.id, { text, final });

            handleLocalMessage(s.id, text, final);
        });
    }, [localMessages.segments, handleLocalMessage, micPublication, local, lkLocalParticipant]);

    return null;
});

export { TranscriptionTile };
